// Ficha de ingrediente (modal): la misma que abre la página Ingredientes, para
// usarla también desde la lista de compra y la compra de la semana.
//
// Props:
// - abierto: si se muestra.
// - ingrediente: el ingrediente a editar (objeto). Sin él y sin ingredienteId,
//   alta de un ingrediente nuevo.
// - ingredienteId: si no se pasa el objeto, se carga la ficha por id.
// - onCerrar(): al cerrar o cancelar.
// - onGuardado(ingrediente): tras guardar (el modal se cierra solo después).
// - proveedores / alergenos: listas ya cargadas (opcional; si no, las carga).
// - onProveedoresCambiados(): tras crear un proveedor rápido (la página
//   Ingredientes recarga sus listas).
// - etiquetas: respuesta de etiquetasAPI.productos('ingrediente') ya cargada
//   (opcional; si no llega, el modal la pide al abrirse). Sirve para la
//   sección "Una vez abierto" (cómo se conserva abierto, cuántos días dura o si
//   dura hasta la fecha del envase, y de dónde sale ese plazo), que se guarda
//   aparte en la configuración de Etiquetas y solo si se toca.
//
// Mismos campos, validaciones y guardado que tenía la página; además la
// sección de compra y la merma (fase 6 de la lista de compra).
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { ingredientesAPI, proveedoresAPI, alergenosAPI, etiquetasAPI } from '../../api/api';
import { Plus, Edit, AlertTriangle, Package, Beef, Milk, CheckCircle2, FileText, Wine, ShoppingCart, Tag } from 'lucide-react';
import AlergenoIcon, { AlergenoList } from '../AlergenoIcon';
import { toast } from '../../hooks/use-toast';
import {
  AVISO_SUPERA_APPCC, CONSERVACIONES, NOMBRE_CONSERVACION, REFRIGERADO, TEXTO_HASTA_ENVASE,
  TOPE_REFRIGERADO_DIAS_APPCC, VIDA_MODO_DIAS, VIDA_MODO_ENVASE,
} from '../../utils/etiquetas';
import { FuenteProducto } from '../etiquetas/ProductosTab';

const MAX_DIAS_ABIERTO = 3650;
const ABIERTO_VACIO = { conservacion: '', vida_modo: '', vida_dias: '' };

const abiertoDesdeConfig = (config) => (config
  ? {
    conservacion: config.conservacion || '',
    vida_modo: config.vida_modo || VIDA_MODO_DIAS,
    vida_dias: config.vida_dias === null || config.vida_dias === undefined ? '' : String(config.vida_dias),
  }
  : ABIERTO_VACIO);

// Secciones de la lista de compra (necesidades_evento.SECCIONES); vacío = automática
export const SECCIONES_COMPRA = [
  { clave: 'frescos', texto: 'Frescos' },
  { clave: 'secos', texto: 'Secos' },
  { clave: 'bebidas', texto: 'Bebidas' },
  { clave: 'desechables', texto: 'Desechables' },
  { clave: 'servicio', texto: 'Servicio' },
];
export const textoSeccionCompra = (clave) => (SECCIONES_COMPRA.find(s => s.clave === clave) || {}).texto || '';

const FORM_VACIO = {
  nombre: '',
  proveedor_id: '',
  precio_unitario: 0,
  unidad: 'kg',
  precio_paquete: 0,
  unidades_por_paquete: 1,
  unidad_consumible: '',
  alergenos_ids: [],
  trazas_alergenos_ids: [],
  carne: false,
  origen_animal: false,
  sin_lactosa: false,
  contiene_cerdo: false,
  no_apto_embarazadas: false,
  con_alcohol: false,
  activo: true,
  aprovechable_entre_eventos: true,
  tipo_iva: 10,
  seccion_compra: '',
  merma_pct: 0,
};

const formDesdeIngrediente = (ingrediente) => ({
  nombre: ingrediente.nombre,
  proveedor_id: ingrediente.proveedor_id || '',
  precio_unitario: ingrediente.precio_unitario || 0,
  unidad: ingrediente.unidad || 'kg',
  precio_paquete: ingrediente.precio_paquete || 0,
  unidades_por_paquete: ingrediente.unidades_por_paquete || 1,
  unidad_consumible: ingrediente.unidad_consumible == null ? '' : String(ingrediente.unidad_consumible),
  alergenos_ids: ingrediente.alergenos_ids || [],
  trazas_alergenos_ids: ingrediente.trazas_alergenos_ids || [],
  carne: ingrediente.carne || false,
  origen_animal: ingrediente.origen_animal || false,
  sin_lactosa: ingrediente.sin_lactosa || false,
  contiene_cerdo: ingrediente.contiene_cerdo || false,
  no_apto_embarazadas: ingrediente.no_apto_embarazadas || false,
  con_alcohol: !!ingrediente.con_alcohol,
  activo: ingrediente.activo !== undefined ? ingrediente.activo : true,
  aprovechable_entre_eventos: ingrediente.aprovechable_entre_eventos !== undefined ? ingrediente.aprovechable_entre_eventos : true,
  tipo_iva: ingrediente.tipo_iva || 10,
  seccion_compra: ingrediente.seccion_compra || '',
  merma_pct: ingrediente.merma_pct == null ? 0 : ingrediente.merma_pct,
});

const textoError = (error) => {
  if (error?.response?.data?.detail) {
    const detalle = error.response.data.detail;
    if (typeof detalle === 'string') return detalle;
    if (Array.isArray(detalle)) return detalle.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
    return JSON.stringify(detalle);
  }
  return error?.message || 'Error desconocido';
};

const IngredienteModal = ({
  abierto, ingrediente = null, ingredienteId = null, onCerrar, onGuardado,
  proveedores: proveedoresProp = null, alergenos: alergenosProp = null, onProveedoresCambiados = null,
  etiquetas: etiquetasProp = null,
}) => {
  const [editingIngrediente, setEditingIngrediente] = useState(null);
  const [formData, setFormData] = useState(FORM_VACIO);
  const [cargandoFicha, setCargandoFicha] = useState(false);
  const [errorFicha, setErrorFicha] = useState(null);
  const [proveedoresPropios, setProveedoresPropios] = useState([]);
  const [alergenosPropios, setAlergenosPropios] = useState([]);
  const [precioConIvaInput, setPrecioConIvaInput] = useState(''); // Estado separado para edición de precio con IVA
  const [editandoPrecioConIva, setEditandoPrecioConIva] = useState(false); // Flag para saber si se está editando el campo con IVA
  const [platosIngrediente, setPlatosIngrediente] = useState([]);
  const [platosIngredienteLoading, setPlatosIngredienteLoading] = useState(false);
  const [platosIngredienteMeta, setPlatosIngredienteMeta] = useState({});
  const [platosIngredienteAnio, setPlatosIngredienteAnio] = useState(new Date().getFullYear());
  // "Una vez abierto": configuración de Etiquetas del ingrediente
  const [etiquetasPropias, setEtiquetasPropias] = useState(null);
  const [etiquetasEstado, setEtiquetasEstado] = useState('ok'); // 'cargando' | 'error' | 'ok'
  const [abiertoForm, setAbiertoForm] = useState(ABIERTO_VACIO);
  const [abiertoTocado, setAbiertoTocado] = useState(false);

  const proveedores = proveedoresProp || proveedoresPropios;
  const alergenos = alergenosProp || alergenosPropios;
  const conProveedoresProp = !!proveedoresProp;
  const conAlergenosProp = !!alergenosProp;
  const conEtiquetasProp = !!etiquetasProp;
  const datosEtiquetas = etiquetasProp || etiquetasPropias;
  const estadoEtiquetas = conEtiquetasProp ? 'ok' : etiquetasEstado;
  const puedeEditarAbierto = !!datosEtiquetas?.puede_editar;
  const idFicha = editingIngrediente?.id || null;
  const configAbierto = idFicha
    ? (datosEtiquetas?.productos || []).find((p) => p.tipo === 'ingrediente' && p.id === idFicha) || null
    : null;
  const configConservacion = configAbierto?.conservacion ?? null;
  const configVidaDias = configAbierto?.vida_dias ?? null;
  const configVidaModo = configAbierto?.vida_modo ?? null;
  const configConfirmado = configAbierto ? !!configAbierto.confirmado : null;

  // Al abrir: la ficha que llega, la que se carga por id o un alta nueva
  // (antes de pintar, para no enseñar un instante el formulario vacío)
  useLayoutEffect(() => {
    if (!abierto) return undefined;
    let cancelado = false;
    const preparar = (ing) => {
      setEditingIngrediente(ing);
      setFormData(ing ? formDesdeIngrediente(ing) : FORM_VACIO);
      setPrecioConIvaInput('');
      setEditandoPrecioConIva(false);
      setPlatosIngrediente([]);
      setPlatosIngredienteMeta({});
      setPlatosIngredienteAnio(new Date().getFullYear());
    };
    setErrorFicha(null);
    if (ingrediente) {
      preparar(ingrediente);
    } else if (ingredienteId) {
      preparar(null);
      setCargandoFicha(true);
      ingredientesAPI.getOne(ingredienteId)
        .then((res) => { if (!cancelado) preparar(res.data); })
        .catch((err) => { if (!cancelado) setErrorFicha(textoError(err)); })
        .finally(() => { if (!cancelado) setCargandoFicha(false); });
    } else {
      preparar(null);
    }
    return () => { cancelado = true; };
  }, [abierto, ingrediente, ingredienteId]);

  // Proveedores y alérgenos si no los da quien abre el modal
  useEffect(() => {
    if (!abierto || (conProveedoresProp && conAlergenosProp)) return undefined;
    let cancelado = false;
    Promise.all([
      conProveedoresProp ? Promise.resolve(null) : proveedoresAPI.getAll(),
      conAlergenosProp ? Promise.resolve(null) : alergenosAPI.getAll(),
    ]).then(([prov, alerg]) => {
      if (cancelado) return;
      if (prov) setProveedoresPropios(Array.isArray(prov.data) ? prov.data : []);
      if (alerg) setAlergenosPropios(Array.isArray(alerg.data) ? alerg.data : []);
    }).catch((err) => console.error('Error cargando proveedores o alérgenos:', err));
    return () => { cancelado = true; };
  }, [abierto, conProveedoresProp, conAlergenosProp]);

  // Configuración de Etiquetas si no la da quien abre el modal
  useEffect(() => {
    if (!abierto || conEtiquetasProp) return undefined;
    let cancelado = false;
    setEtiquetasEstado('cargando');
    etiquetasAPI.productos('ingrediente')
      .then((res) => {
        if (cancelado) return;
        setEtiquetasPropias(res?.data && Array.isArray(res.data.productos) ? res.data : null);
        setEtiquetasEstado(res?.data ? 'ok' : 'error');
      })
      .catch((err) => {
        if (cancelado) return;
        console.error('Error cargando la conservación de etiquetas:', err);
        setEtiquetasPropias(null);
        setEtiquetasEstado('error');
      });
    return () => { cancelado = true; };
  }, [abierto, conEtiquetasProp]);

  // Valores de "Una vez abierto": los de la configuración (o vacíos en un alta)
  useEffect(() => {
    if (!abierto) return;
    setAbiertoForm(configConservacion === null
      ? ABIERTO_VACIO
      : abiertoDesdeConfig({ conservacion: configConservacion, vida_modo: configVidaModo, vida_dias: configVidaDias }));
    setAbiertoTocado(false);
  }, [abierto, idFicha, configConservacion, configVidaModo, configVidaDias, configConfirmado]);

  useEffect(() => {
    if (!abierto || !editingIngrediente?.id) return undefined;
    let cancelled = false;
    setPlatosIngredienteLoading(true);
    ingredientesAPI.getPlatos(editingIngrediente.id, platosIngredienteAnio)
      .then(res => {
        if (cancelled) return;
        setPlatosIngrediente(res.data?.platos || []);
        setPlatosIngredienteMeta({
          anio: res.data?.anio,
          unidad: res.data?.unidad,
          total_cantidad_anio: res.data?.total_cantidad_anio,
          total_coste_anio: res.data?.total_coste_anio,
        });
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Error cargando consumo del ingrediente:', err);
        setPlatosIngrediente([]);
        setPlatosIngredienteMeta({});
      })
      .finally(() => { if (!cancelled) setPlatosIngredienteLoading(false); });
    return () => { cancelled = true; };
  }, [abierto, editingIngrediente?.id, platosIngredienteAnio]);

  const cambiarAbierto = (campo, valor) => {
    // Poner días en un alta sin modo elegido es elegir "Días"
    setAbiertoForm((prev) => ({
      ...prev,
      [campo]: valor,
      ...(campo === 'vida_dias' && !prev.vida_modo ? { vida_modo: VIDA_MODO_DIAS } : {}),
    }));
    setAbiertoTocado(true);
  };

  // Qué se manda a Etiquetas al guardar: null si no hay nada que guardar
  // (sección sin tocar, o tocada sin cambios en una configuración ya confirmada).
  // "Dar por buena" sin cambiar valores manda mantener_fuente para no perder la
  // fuente sugerida (APPCC); cualquier cambio queda como criterio de cocina.
  const cuerpoAbierto = () => {
    if (!abiertoTocado || !puedeEditarAbierto) return null;
    const original = abiertoDesdeConfig(configAbierto);
    const dias = String(abiertoForm.vida_dias ?? '').trim();
    const hastaEnvase = abiertoForm.vida_modo === VIDA_MODO_ENVASE;
    const cambiado = abiertoForm.conservacion !== original.conservacion
      || abiertoForm.vida_modo !== original.vida_modo
      || (!hastaEnvase && dias !== original.vida_dias);
    if (!cambiado && !(configAbierto && !configAbierto.confirmado)) return null;
    const cuerpo = {};
    if (abiertoForm.conservacion) cuerpo.conservacion = abiertoForm.conservacion;
    if (hastaEnvase) cuerpo.vida_modo = VIDA_MODO_ENVASE;
    else if (dias) {
      cuerpo.vida_modo = VIDA_MODO_DIAS;
      cuerpo.vida_dias = Number(dias);
    }
    if (!configAbierto && !Object.keys(cuerpo).length) return null;
    if (!cambiado) cuerpo.mantener_fuente = true;
    return cuerpo;
  };

  // Días obligatorios si se elige "Días" y no hay un número de antes
  const faltanDiasAbierto = () => abiertoTocado && puedeEditarAbierto
    && abiertoForm.vida_modo === VIDA_MODO_DIAS
    && !String(abiertoForm.vida_dias ?? '').trim();

  const cerrar = () => {
    setEditingIngrediente(null);
    setFormData(FORM_VACIO);
    setPrecioConIvaInput('');
    setEditandoPrecioConIva(false);
    if (onCerrar) onCerrar();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Ensure numeric values are numbers and validate
      const precioPaquete = parseFloat(formData.precio_paquete);
      const unidadesPaquete = parseFloat(formData.unidades_por_paquete);

      // Validate that all numeric fields are valid numbers
      if (isNaN(precioPaquete) || precioPaquete <= 0) {
        alert('El precio del envase debe ser un número válido mayor que 0');
        return;
      }
      if (isNaN(unidadesPaquete) || unidadesPaquete <= 0) {
        alert('Las unidades por envase deben ser un número válido mayor que 0');
        return;
      }
      const mermaRaw = formData.merma_pct;
      const merma = mermaRaw === '' || mermaRaw == null ? 0 : parseFloat(mermaRaw);
      if (isNaN(merma) || merma < 0 || merma > 100) {
        alert('La merma debe ser un número entre 0 y 100');
        return;
      }

      const unidadConsumibleRaw = formData.unidad_consumible;
      const unidadConsumible = unidadConsumibleRaw === '' || unidadConsumibleRaw == null
        ? null
        : (isNaN(parseFloat(unidadConsumibleRaw)) ? null : parseFloat(unidadConsumibleRaw));

      if (faltanDiasAbierto()) {
        alert('Indica cuántos días dura abierto, o elige "Hasta la fecha del envase"');
        return;
      }
      const abiertoBody = cuerpoAbierto();
      if (abiertoBody && abiertoBody.vida_dias !== undefined) {
        const d = abiertoBody.vida_dias;
        if (!Number.isInteger(d) || d < 1 || d > MAX_DIAS_ABIERTO) {
          alert(`Los días que dura abierto deben ser un número entero entre 1 y ${MAX_DIAS_ABIERTO}`);
          return;
        }
      }

      const dataToSend = {
        ...formData,
        precio_unitario: precioPaquete / unidadesPaquete, // Calcular precio unitario
        precio_paquete: precioPaquete,
        unidades_por_paquete: unidadesPaquete,
        unidad_consumible: unidadConsumible,
        seccion_compra: formData.seccion_compra || null,
        merma_pct: merma,
      };

      let guardado;
      if (editingIngrediente) {
        const res = await ingredientesAPI.update(editingIngrediente.id, dataToSend);
        guardado = res?.data;
      } else {
        const res = await ingredientesAPI.create(dataToSend);
        guardado = res?.data;
      }

      // Después, y solo si se tocó, cómo se conserva abierto (configuración de
      // Etiquetas). Si falla, el ingrediente ya está guardado: solo se avisa.
      let abiertoFallo = false;
      const idGuardado = editingIngrediente?.id || guardado?.id;
      if (abiertoBody) {
        try {
          if (!idGuardado) throw new Error('Sin id del ingrediente');
          await etiquetasAPI.guardarProducto('ingrediente', idGuardado, abiertoBody);
        } catch (errorAbierto) {
          console.error('Error guardando la conservación una vez abierto:', errorAbierto);
          abiertoFallo = true;
        }
      }

      alert(editingIngrediente ? 'Ingrediente actualizado correctamente' : 'Ingrediente creado correctamente');
      if (abiertoFallo) {
        toast({
          title: 'Ingrediente guardado, pero no se pudo guardar cómo se conserva abierto.',
          variant: 'destructive',
        });
      }

      if (onGuardado) await onGuardado(guardado);
      cerrar();
    } catch (error) {
      console.error('Error saving ingrediente:', error);
      alert(`Error al guardar el ingrediente: ${textoError(error)}`);
    }
  };

  const handleCrearProveedorRapido = async (nombreProveedor) => {
    try {
      const nuevoProveedor = {
        nombre: nombreProveedor,
        contacto: '',
        telefono: '',
        email: '',
        direccion: ''
      };
      const response = await proveedoresAPI.create(nuevoProveedor);
      if (onProveedoresCambiados) {
        await onProveedoresCambiados();
      }
      if (!conProveedoresProp) {
        const prov = await proveedoresAPI.getAll();
        setProveedoresPropios(Array.isArray(prov.data) ? prov.data : []);
      }
      setFormData(prev => ({ ...prev, proveedor_id: response.data.id }));
      alert(`Proveedor "${nombreProveedor}" creado`);
    } catch (error) {
      console.error('Error creating proveedor:', error);
      alert('Error al crear el proveedor');
    }
  };

  const toggleAlergeno = (alergenoId) => {
    setFormData(prev => ({
      ...prev,
      alergenos_ids: prev.alergenos_ids.includes(alergenoId)
        ? prev.alergenos_ids.filter(id => id !== alergenoId)
        : [...prev.alergenos_ids, alergenoId],
      // Si se añade a alérgenos, quitar de trazas (no puede estar en ambos)
      trazas_alergenos_ids: prev.trazas_alergenos_ids.filter(id => id !== alergenoId)
    }));
  };

  const toggleTrazaAlergeno = (alergenoId) => {
    setFormData(prev => ({
      ...prev,
      trazas_alergenos_ids: prev.trazas_alergenos_ids.includes(alergenoId)
        ? prev.trazas_alergenos_ids.filter(id => id !== alergenoId)
        : [...prev.trazas_alergenos_ids, alergenoId],
      // Si se añade a trazas, quitar de alérgenos (no puede estar en ambos)
      alergenos_ids: prev.alergenos_ids.filter(id => id !== alergenoId)
    }));
  };

  if (!abierto) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="ingrediente-modal">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-200 bg-duetto-primary bg-opacity-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editingIngrediente || ingrediente || ingredienteId ? 'Editar ingrediente' : 'Nuevo ingrediente'}
              </h2>
              {editingIngrediente && (
                <p className="text-sm text-gray-600 mt-1">{editingIngrediente.nombre}</p>
              )}
            </div>
            <button
              type="button"
              onClick={cerrar}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Cerrar"
            >
              <span className="text-gray-500 text-2xl leading-none">×</span>
            </button>
          </div>
        </div>

        {cargandoFicha && <p className="p-6 text-sm text-gray-500">Cargando la ficha...</p>}
        {errorFicha && <p className="p-6 text-sm text-red-700">No se pudo cargar el ingrediente: {errorFicha}</p>}

        {!cargandoFicha && !errorFicha && (
          <form onSubmit={handleSubmit} className="p-6">
            {/* Basic Info Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Package className="w-5 h-5 mr-2 text-duetto-primary" />
                Información básica
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre del ingrediente *
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    placeholder="Ej: Tomate maduro, Pechuga de pollo..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Proveedor</label>
                  <select
                    value={formData.proveedor_id}
                    onChange={(e) => {
                      if (e.target.value === 'CREAR_NUEVO') {
                        const nuevoProveedor = prompt('Nombre del nuevo proveedor:');
                        if (nuevoProveedor && nuevoProveedor.trim()) {
                          handleCrearProveedorRapido(nuevoProveedor.trim());
                        }
                      } else {
                        setFormData({ ...formData, proveedor_id: e.target.value });
                      }
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  >
                    <option value="">Sin proveedor asignado</option>
                    <option value="CREAR_NUEVO" className="text-green-600 font-semibold">+ Crear nuevo proveedor</option>
                    {[...proveedores].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(prov => (
                      <option key={prov.id} value={prov.id}>{prov.nombre}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Unidad de medida *</label>
                  <select
                    value={formData.unidad}
                    onChange={(e) => setFormData({ ...formData, unidad: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                  >
                    <option value="g">Gramos (g)</option>
                    <option value="kg">Kilogramos (kg)</option>
                    <option value="L">Litros (L)</option>
                    <option value="ml">Mililitros (ml)</option>
                    <option value="ud">Unidad (ud)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Información de precios
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de IVA *</label>
                  <select
                    value={formData.tipo_iva || 10}
                    onChange={(e) => setFormData({ ...formData, tipo_iva: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    required
                  >
                    <option value="4">4% básico</option>
                    <option value="10">10% preparado</option>
                    <option value="21">21% general</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Envase sin IVA (€) *</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={formData.precio_paquete}
                    onChange={(e) => setFormData({ ...formData, precio_paquete: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Envase con IVA (€)</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={editandoPrecioConIva
                      ? precioConIvaInput
                      : (formData.precio_paquete ? (parseFloat(formData.precio_paquete) * (1 + (formData.tipo_iva || 10) / 100)).toFixed(2) : '')}
                    onFocus={(e) => {
                      setEditandoPrecioConIva(true);
                      setPrecioConIvaInput(e.target.value);
                    }}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      setPrecioConIvaInput(inputValue);
                      const precioConIva = parseFloat(inputValue) || 0;
                      if (precioConIva > 0) {
                        const precioSinIva = precioConIva / (1 + (formData.tipo_iva || 10) / 100);
                        setFormData(prev => ({ ...prev, precio_paquete: precioSinIva.toFixed(2) }));
                      } else if (inputValue === '' || inputValue === '0') {
                        setFormData(prev => ({ ...prev, precio_paquete: '' }));
                      }
                    }}
                    onBlur={() => setEditandoPrecioConIva(false)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-emerald-50"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Uds. por envase *</label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={formData.unidades_por_paquete}
                    onChange={(e) => setFormData({ ...formData, unidades_por_paquete: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    placeholder="1"
                    required
                  />
                </div>
              </div>

              <p className="text-xs text-gray-600 mt-3">
                Introduce el precio con o sin IVA; el otro se calcula automáticamente.
                {formData.precio_paquete > 0 && formData.unidades_por_paquete > 0 && (
                  <>
                    {' '}Precio por unidad:{' '}
                    <span className="font-semibold text-gray-800">
                      €{(parseFloat(formData.precio_paquete) / parseFloat(formData.unidades_por_paquete)).toFixed(4)} / {formData.unidad}
                    </span>
                    {' '}(sin IVA)
                  </>
                )}
              </p>

              <label className="mt-4 flex items-start gap-3 p-3 bg-white rounded-md border border-green-300 cursor-pointer hover:bg-green-50 transition-colors">
                <input
                  type="checkbox"
                  checked={!!formData.aprovechable_entre_eventos}
                  onChange={(e) => setFormData({ ...formData, aprovechable_entre_eventos: e.target.checked })}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">Sobrante aprovechable entre eventos</span>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Actívalo para harina, aceite, especias… Desactívalo para productos que si sobran se descartan (tortilla entera, caja de bebida abierta, etc.). Cuando está desactivado, el coste del presupuesto se calcula a envase entero y los sobrantes no cuentan como stock en el próximo evento.
                  </p>
                </div>
              </label>

              {/* Unidad consumible mínima: solo aplica cuando NO es aprovechable */}
              {!formData.aprovechable_entre_eventos && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <label className="block text-sm font-medium text-gray-800 mb-1">
                    Unidad consumible mínima <span className="text-xs font-normal text-gray-500">(opcional)</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    min="0"
                    value={formData.unidad_consumible}
                    onChange={(e) => setFormData({ ...formData, unidad_consumible: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder={`Vacío = envase entero (${formData.unidades_por_paquete || 1} ${formData.unidad || 'ud'})`}
                  />
                  <p className="text-xs text-gray-600 mt-1">
                    Cuántas <b>{formData.unidad || 'unidades'}</b> se abren juntas en una unidad indivisible (lo que no se reaprovecha). Ejemplos:
                  </p>
                  <ul className="text-xs text-gray-600 mt-1 ml-4 list-disc space-y-0.5">
                    <li>Brick de soja 1L dentro de caja de 6L: <b>1</b> (cada brick es 1L y se abre por separado)</li>
                    <li>Tortilla 600g cortada en 20 raciones: <b>20</b> (toda la tortilla se sirve junta, sobrantes se tiran)</li>
                  </ul>
                  <p className="text-xs text-gray-500 mt-1">
                    Si lo dejas vacío, se redondea al envase completo de compra ({formData.unidades_por_paquete || 1} {formData.unidad || 'ud'}).
                  </p>
                </div>
              )}
            </div>

            {/* Lista de compra: sección y merma */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <ShoppingCart className="w-5 h-5 mr-2 text-duetto-primary" />
                Lista de compra
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="ingrediente-seccion-compra">Sección de compra</label>
                  <select
                    id="ingrediente-seccion-compra"
                    value={formData.seccion_compra || ''}
                    onChange={(e) => setFormData({ ...formData, seccion_compra: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    data-testid="ingrediente-seccion-compra"
                  >
                    <option value="">Automática</option>
                    {SECCIONES_COMPRA.map(s => <option key={s.clave} value={s.clave}>{s.texto}</option>)}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Automática: la lista la deduce del tipo de plato y del ingrediente.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="ingrediente-merma">Merma (%)</label>
                  <input
                    id="ingrediente-merma"
                    type="number"
                    step="any"
                    min="0"
                    max="100"
                    value={formData.merma_pct}
                    onChange={(e) => setFormData({ ...formData, merma_pct: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    placeholder="0"
                    data-testid="ingrediente-merma"
                  />
                  <p className="text-xs text-gray-500 mt-1">Solo afecta a la compra, no a la receta.</p>
                </div>
              </div>
            </div>

            {/* Una vez abierto: configuración de Etiquetas (fuente única) */}
            {(() => {
              const sinDatos = estadoEtiquetas !== 'ok' || !datosEtiquetas;
              const bloqueado = sinDatos || !puedeEditarAbierto;
              let nota;
              if (estadoEtiquetas === 'cargando') nota = 'Cargando cómo se conserva abierto...';
              else if (sinDatos) nota = 'No se pudo cargar cómo se conserva abierto. El resto de la ficha se guarda igual.';
              else if (!idFicha) nota = 'Se propondrá por su nombre al guardar; puedes fijarla ya.';
              else if (!configAbierto) nota = 'No sale en Etiquetas (ingrediente inactivo). Se propondrá por su nombre; puedes fijarla ya.';
              else if (!configAbierto.confirmado) nota = `Propuesta por grupo (${configAbierto.grupo || 'Otros'}), sin confirmar. Al guardar queda confirmada.`;
              else nota = configAbierto.confirmado_por?.nombre ? `Confirmado por ${configAbierto.confirmado_por.nombre}.` : 'Confirmado.';
              const pedirConfirmar = !bloqueado && configAbierto && !configAbierto.confirmado;
              const hastaEnvase = abiertoForm.vida_modo === VIDA_MODO_ENVASE;
              // Aviso del APPCC: con la sección sin tocar, el del servidor (que
              // respeta el plazo del fabricante); al tocarla, lo que se guardaría
              // (criterio de cocina)
              const superaAppcc = abiertoTocado
                ? (abiertoForm.conservacion === REFRIGERADO && !hastaEnvase
                  && Number(abiertoForm.vida_dias) > TOPE_REFRIGERADO_DIAS_APPCC)
                : !!configAbierto?.supera_appcc;
              return (
                <div className="mb-6 p-4 bg-sky-50 rounded-lg border border-sky-200" data-testid="ingrediente-una-vez-abierto">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Tag className="w-5 h-5 mr-2 text-duetto-primary" />
                    Una vez abierto
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="ingrediente-abierto-conservacion">Se conserva en</label>
                      <select
                        id="ingrediente-abierto-conservacion"
                        value={abiertoForm.conservacion}
                        onChange={(e) => cambiarAbierto('conservacion', e.target.value)}
                        disabled={bloqueado}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-duetto-primary disabled:opacity-60"
                        data-testid="ingrediente-abierto-conservacion"
                      >
                        {!configAbierto && <option value="">Sin fijar</option>}
                        {CONSERVACIONES.map((c) => <option key={c} value={c}>{NOMBRE_CONSERVACION[c]}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="ingrediente-abierto-modo">Dura abierto</label>
                      <select
                        id="ingrediente-abierto-modo"
                        value={abiertoForm.vida_modo}
                        onChange={(e) => cambiarAbierto('vida_modo', e.target.value)}
                        disabled={bloqueado}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-duetto-primary disabled:opacity-60"
                        data-testid="ingrediente-abierto-modo"
                      >
                        {!configAbierto && <option value="">Sin fijar</option>}
                        <option value={VIDA_MODO_DIAS}>Días</option>
                        <option value={VIDA_MODO_ENVASE}>{TEXTO_HASTA_ENVASE}</option>
                      </select>
                    </div>
                    {!hastaEnvase && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="ingrediente-abierto-dias">Dura abierto (días)</label>
                      <input
                        id="ingrediente-abierto-dias"
                        type="number"
                        inputMode="numeric"
                        step="1"
                        min="1"
                        max={MAX_DIAS_ABIERTO}
                        value={abiertoForm.vida_dias}
                        onChange={(e) => cambiarAbierto('vida_dias', e.target.value)}
                        disabled={bloqueado}
                        placeholder={configAbierto ? '' : 'Sin fijar'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-duetto-primary disabled:opacity-60"
                        data-testid="ingrediente-abierto-dias"
                      />
                    </div>
                    )}
                  </div>
                  {superaAppcc && (
                    <p className="text-xs mt-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1" data-testid="ingrediente-abierto-supera-appcc">
                      {AVISO_SUPERA_APPCC}
                    </p>
                  )}
                  {configAbierto && <FuenteProducto producto={configAbierto} className="mt-2" />}
                  <p className={`text-xs mt-2 ${configAbierto && !configAbierto.confirmado ? 'text-amber-800' : 'text-gray-600'}`}>
                    {nota}
                    {pedirConfirmar && !abiertoTocado && (
                      <>
                        {' '}
                        <button
                          type="button"
                          onClick={() => setAbiertoTocado(true)}
                          className="font-semibold text-duetto-primary underline hover:no-underline"
                          data-testid="ingrediente-abierto-dar-por-buena"
                        >
                          Dar por buena
                        </button>
                      </>
                    )}
                    {pedirConfirmar && abiertoTocado && ' Se confirmará al guardar.'}
                  </p>
                  {!sinDatos && !puedeEditarAbierto && (
                    <p className="text-xs text-gray-500 mt-1">Solo dirección y cocina pueden cambiar cómo se conserva abierto.</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Es lo que usa la página Etiquetas para la caducidad del producto abierto.</p>
                </div>
              );
            })()}

            {/* Special Characteristics Section */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Características especiales
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { key: 'carne', label: 'Contiene carne', Icon: Beef, active: 'bg-red-100 border-red-400 text-red-800', iconActive: 'text-red-600' },
                  { key: 'origen_animal', label: 'Origen animal', Icon: Milk, active: 'bg-orange-100 border-orange-400 text-orange-800', iconActive: 'text-orange-600' },
                  { key: 'sin_lactosa', label: 'Sin lactosa', Icon: CheckCircle2, active: 'bg-cyan-100 border-cyan-400 text-cyan-800', iconActive: 'text-cyan-600' },
                  { key: 'contiene_cerdo', label: 'Contiene cerdo', Icon: AlertTriangle, active: 'bg-pink-100 border-pink-400 text-pink-800', iconActive: 'text-pink-600' },
                  { key: 'no_apto_embarazadas', label: 'No apto embarazadas', Icon: AlertTriangle, active: 'bg-purple-100 border-purple-400 text-purple-800', iconActive: 'text-purple-600' },
                  { key: 'con_alcohol', label: 'Con alcohol', Icon: Wine, active: 'bg-amber-100 border-amber-400 text-amber-800', iconActive: 'text-amber-600' },
                ].map(({ key, label, Icon, active, iconActive }) => (
                  <label
                    key={key}
                    className={`flex items-center gap-2 p-3 border rounded-lg cursor-pointer transition-all text-sm ${
                      formData[key]
                        ? active + ' shadow-sm'
                        : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={!!formData[key]}
                      onChange={(e) => setFormData({ ...formData, [key]: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                    />
                    <Icon className={`w-4 h-4 ${formData[key] ? iconActive : 'text-gray-400'}`} />
                    <span className="font-medium">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Allergens Section - CONTIENE */}
            <div className="mb-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-amber-600" />
                Alérgenos que contiene
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Selecciona los alérgenos presentes de forma directa en este ingrediente
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {alergenos.map(alergeno => (
                  <label
                    key={alergeno.id}
                    className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-all ${
                      formData.alergenos_ids.includes(alergeno.id)
                        ? 'bg-amber-100 border-amber-400 shadow-sm ring-2 ring-amber-300'
                        : formData.trazas_alergenos_ids.includes(alergeno.id)
                          ? 'bg-gray-100 border-gray-300 opacity-50 cursor-not-allowed'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.alergenos_ids.includes(alergeno.id)}
                      onChange={() => toggleAlergeno(alergeno.id)}
                      disabled={formData.trazas_alergenos_ids.includes(alergeno.id)}
                      className="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <AlergenoIcon nombre={alergeno.nombre} size="sm" />
                    <span className="text-xs font-medium text-gray-900">{alergeno.nombre}</span>
                  </label>
                ))}
              </div>
              {formData.alergenos_ids.length > 0 && (
                <div className="mt-3 p-2 bg-white rounded border border-amber-300">
                  <p className="text-xs text-amber-700 font-medium mb-1">Contiene:</p>
                  <AlergenoList
                    alergenos={formData.alergenos_ids.map(id => alergenos.find(a => a.id === id)).filter(Boolean)}
                    size="sm"
                    showLabels={false}
                  />
                </div>
              )}
            </div>

            {/* Allergens Section - TRAZAS */}
            <div className="mb-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                <AlertTriangle className="w-5 h-5 mr-2 text-orange-500" />
                Puede contener trazas de
              </h3>
              <p className="text-xs text-gray-600 mb-3">
                Selecciona los alérgenos que pueden estar presentes por contaminación cruzada
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {alergenos.map(alergeno => (
                  <label
                    key={`traza-${alergeno.id}`}
                    className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-all ${
                      formData.trazas_alergenos_ids.includes(alergeno.id)
                        ? 'bg-orange-100 border-orange-400 shadow-sm ring-2 ring-orange-300'
                        : formData.alergenos_ids.includes(alergeno.id)
                          ? 'bg-gray-100 border-gray-300 opacity-50 cursor-not-allowed'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.trazas_alergenos_ids.includes(alergeno.id)}
                      onChange={() => toggleTrazaAlergeno(alergeno.id)}
                      disabled={formData.alergenos_ids.includes(alergeno.id)}
                      className="rounded border-gray-300 text-orange-500 focus:ring-orange-400"
                    />
                    <AlergenoIcon nombre={alergeno.nombre} size="sm" />
                    <span className="text-xs font-medium text-gray-900">{alergeno.nombre}</span>
                  </label>
                ))}
              </div>
              {formData.trazas_alergenos_ids.length > 0 && (
                <div className="mt-3 p-2 bg-white rounded border border-orange-300">
                  <p className="text-xs text-orange-700 font-medium mb-1">Puede contener trazas:</p>
                  <AlergenoList
                    alergenos={formData.trazas_alergenos_ids.map(id => alergenos.find(a => a.id === id)).filter(Boolean)}
                    size="sm"
                    showLabels={false}
                  />
                </div>
              )}
            </div>

            {/* Resumen de alérgenos */}
            {formData.alergenos_ids.length === 0 && formData.trazas_alergenos_ids.length === 0 && (
              <div className="mb-6 p-3 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 flex items-center">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Este ingrediente no tiene alérgenos ni trazas registradas
                </p>
              </div>
            )}

            {/* Status Section */}
            <div className="mb-6">
              <label className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors">
                <input
                  type="checkbox"
                  checked={formData.activo}
                  onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
                  className="w-5 h-5 rounded border-gray-300 text-duetto-primary focus:ring-duetto-primary"
                />
                <div>
                  <span className="text-sm font-semibold text-gray-900">Ingrediente activo</span>
                  <p className="text-xs text-gray-500">Los ingredientes inactivos no aparecerán en las recetas</p>
                </div>
              </label>
            </div>

            {/* Platos que usan este ingrediente */}
            {editingIngrediente && (
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-1 gap-3">
                  <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-duetto-primary" />
                    Consumo en platos
                    {!platosIngredienteLoading && (
                      <span className="text-xs font-normal text-gray-500">({platosIngrediente.length})</span>
                    )}
                  </h3>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-600">Año:</label>
                    <select
                      value={platosIngredienteAnio}
                      onChange={(e) => setPlatosIngredienteAnio(parseInt(e.target.value, 10))}
                      className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-duetto-primary"
                    >
                      {Array.from({ length: (new Date().getFullYear() - 2026) + 1 }, (_, i) => 2026 + i).map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">
                  Cantidad y coste del ingrediente en presupuestos confirmados o facturados del año seleccionado, agrupado por plato.
                </p>
                {platosIngredienteLoading ? (
                  <p className="text-xs text-gray-500">Cargando…</p>
                ) : platosIngrediente.length === 0 ? (
                  <p className="text-xs text-gray-500">Ningún plato usa este ingrediente todavía.</p>
                ) : (
                  <div className="overflow-hidden border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plato</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Consumo {platosIngredienteMeta.anio || ''}</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Coste {platosIngredienteMeta.anio || ''}</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {platosIngrediente.map(p => (
                          <tr key={p.id} className={p.activo ? '' : 'bg-gray-50 text-gray-500'}>
                            <td className="px-3 py-2">
                              <div className="flex items-center gap-2">
                                <span className={p.activo ? 'text-gray-900' : 'line-through'}>{p.nombre}</span>
                                {!p.activo && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700 font-semibold uppercase">Inactivo</span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 py-2 text-right text-gray-700 whitespace-nowrap">
                              {Number(p.cantidad_anio || 0).toLocaleString('es-ES', { maximumFractionDigits: 3 })} {p.unidad}
                            </td>
                            <td className="px-3 py-2 text-right font-medium text-gray-900 whitespace-nowrap">
                              €{Number(p.coste_anio || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td className="px-3 py-2 text-xs font-semibold text-gray-700 uppercase">Total</td>
                          <td className="px-3 py-2 text-right text-xs font-semibold text-gray-900 whitespace-nowrap">
                            {Number(platosIngredienteMeta.total_cantidad_anio || 0).toLocaleString('es-ES', { maximumFractionDigits: 3 })} {platosIngredienteMeta.unidad || ''}
                          </td>
                          <td className="px-3 py-2 text-right text-xs font-bold text-duetto-primary whitespace-nowrap">
                            €{Number(platosIngredienteMeta.total_coste_anio || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={cerrar}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-duetto-primary text-white rounded-lg hover:bg-opacity-90 font-medium transition-colors flex items-center space-x-2"
              >
                {editingIngrediente ? (
                  <>
                    <Edit className="w-4 h-4" />
                    <span>Actualizar ingrediente</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    <span>Crear ingrediente</span>
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default IngredienteModal;
